import { useEffect, useMemo } from "react";

import { useRealTimeTypedQuery } from "components";
import { typedQueryClient } from "hooks";
import { useDealerDocumentationsFilters } from "modules/DealerDocumentations/hooks/useDealerDocumentationsFilters";
import { DealerDocumentationsQueryKeys } from "modules/DealerDocumentations/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type { DealerDocumentation, DealerDocumentationDealer, DealerDocumentationLocation } from "models";

const sortDocumentations = (documentations: DealerDocumentation[]) =>
  documentations.toSorted((a, b) => {
    if (a.is_pinned !== b.is_pinned) return b.is_pinned ? 1 : -1;
    return (b.publish_date ?? b.created_on).localeCompare(a.publish_date ?? a.created_on);
  });

export const useDealersDocumentationsQuery = () => {
  const { dealer_ids, dealer_location_ids, visible_to_mechanic } = useDealerDocumentationsFilters();

  const listeners = useMemo(() => {
    const documentationListeners: WebSocketMessageListener<DealerDocumentation>[] = [
      {
        model: "DealerDocumentation",
        action: "upsert",
        callback: ({ data: documentation }) => {
          const documentations = typedQueryClient.getQueryData(DealerDocumentationsQueryKeys.list);
          if (!documentations) return;

          const documentationIdx = documentations.findIndex(doc => doc.id === documentation.id);
          typedQueryClient.setQueryData(
            DealerDocumentationsQueryKeys.list,
            documentationIdx > -1 ? documentations.with(documentationIdx, { ...documentations[documentationIdx], ...documentation }) : [...documentations, documentation]
          );
        }
      }
    ];

    const documentationDealerListeners: WebSocketMessageListener<DealerDocumentationDealer>[] = [
      {
        model: "DealerDocumentationDealer",
        action: "delete",
        callback: ({ data: { dealer_documentation_id, dealer_id } }) => {
          const documentations = typedQueryClient.getQueryData(DealerDocumentationsQueryKeys.list);
          if (!documentations) return;

          const documentationIdx = documentations.findIndex(doc => doc.id === dealer_documentation_id);
          if (documentationIdx === -1) return;

          const documentation = documentations[documentationIdx];
          if (!documentation.dealer_ids?.length) return;

          const dealerIdx = documentation.dealer_ids.findIndex(id => id === dealer_id);
          if (dealerIdx === -1) return;

          typedQueryClient.setQueryData(
            DealerDocumentationsQueryKeys.list,
            documentations.with(documentationIdx, { ...documentation, dealer_ids: documentation.dealer_ids.toSpliced(dealerIdx, 1) })
          );
        }
      }
    ];

    const documentationDealerLocationListeners: WebSocketMessageListener<DealerDocumentationLocation>[] = [
      {
        model: "DealerDocumentationLocation",
        action: "delete",
        callback: ({ data: { dealer_documentation_id, dealer_location_id } }) => {
          const documentations = typedQueryClient.getQueryData(DealerDocumentationsQueryKeys.list);
          if (!documentations) return;

          const documentationIdx = documentations.findIndex(doc => doc.id === dealer_documentation_id);
          if (documentationIdx === -1) return;

          const documentation = documentations[documentationIdx];
          if (!documentation.dealer_location_ids?.length) return;

          const locationIdx = documentation.dealer_location_ids.findIndex(id => id === dealer_location_id);
          if (locationIdx === -1) return;

          typedQueryClient.setQueryData(
            DealerDocumentationsQueryKeys.list,
            documentations.with(documentationIdx, { ...documentation, dealer_location_ids: documentation.dealer_location_ids.toSpliced(locationIdx, 1) })
          );
        }
      }
    ];

    return [...documentationListeners, ...documentationDealerListeners, ...documentationDealerLocationListeners];
  }, []);

  const query = useRealTimeTypedQuery({
    queryKey: DealerDocumentationsQueryKeys.list,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, { dealer_ids, dealer_location_ids, visible_to_mechanic }, baseUrl)).data.documentations,
    select: sortDocumentations,
    listeners
  });

  useEffect(() => {
    if (dealer_ids.length > 0 || dealer_location_ids.length > 0) query.refetch();
  }, [dealer_ids, dealer_location_ids, visible_to_mechanic]);

  return query;
};
